import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES, CATEGORY_LIST } from "@/lib/leadMeta";
import { fileUrl, initials } from "@/lib/fileUrl";
import { downloadBlob } from "@/lib/download";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Building2, Network, Search, UserPlus, Check, X, Clock, Trash2, Plus, Users2, ListChecks, ChevronDown, ChevronRight, LayoutGrid, List, Loader2, ShieldAlert, UserCog, Download } from "lucide-react";

const TypeBadges = ({ types }) => (
  <div className="flex flex-wrap gap-1">
    {(types || []).map((t) => {
      const c = CATEGORIES[t]; if (!c) return null;
      return <span key={t} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium" style={{ backgroundColor: c.color + "1f", color: c.color }}><c.icon className="h-3 w-3" /> {c.label}</span>;
    })}
    {(!types || types.length === 0) && <span className="text-xs text-muted-foreground">No products listed</span>}
  </div>
);

function AddAgencyDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", owner_name: "", owner_email: "", owner_password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async () => {
    if (!form.name.trim() || !form.owner_email.trim() || !form.owner_name.trim() || form.owner_password.length < 6)
      return toast.error("Fill all fields (password min. 6 chars)");
    setLoading(true);
    try {
      await api.post("/superadmin/agencies", form);
      toast.success(`Agency "${form.name}" created`);
      setForm({ name: "", owner_name: "", owner_email: "", owner_password: "" });
      setOpen(false); onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="sa-add-agency-button"><Plus className="mr-2 h-4 w-4" /> Add agency</Button></DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Create a new agency</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Sets up a workspace and its admin owner.</p>
        <div className="space-y-4">
          <div><Label>Agency name</Label><Input value={form.name} onChange={set("name")} className="mt-1.5" data-testid="sa-agency-name-input" placeholder="Acme Insurance" /></div>
          <div><Label>Owner name</Label><Input value={form.owner_name} onChange={set("owner_name")} className="mt-1.5" data-testid="sa-agency-owner-name-input" placeholder="Jane Doe" /></div>
          <div><Label>Owner email</Label><Input type="email" value={form.owner_email} onChange={set("owner_email")} className="mt-1.5" data-testid="sa-agency-owner-email-input" placeholder="jane@acme.com" /></div>
          <div><Label>Temporary password</Label><Input value={form.owner_password} onChange={set("owner_password")} className="mt-1.5" data-testid="sa-agency-owner-password-input" placeholder="Min. 6 characters" /></div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={loading} data-testid="sa-save-agency-button">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create agency</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SuperAdminAgencies() {
  const [agencies, setAgencies] = useState([]);
  const [view, setView] = useState("list");
  const [q, setQ] = useState("");
  const load = () => api.get("/superadmin/agencies").then(({ data }) => setAgencies(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const exportCsv = async () => {
    try {
      const res = await api.get("/superadmin/agencies/export", { responseType: "blob" });
      downloadBlob(res.data, "agencies.csv", "text/csv");
      toast.success("Agencies exported");
    } catch { toast.error("Export failed — please try again"); }
  };
  const impersonate = async (uid) => {
    if (!uid) return toast.error("This agency has no owner to impersonate");
    try { await api.post(`/superadmin/impersonate/${uid}`); toast.success("Impersonation started"); window.location.href = "/app"; }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (o) => {
    if (!window.confirm(`Delete "${o.name}"? This permanently removes its users, leads and territories.`)) return;
    try { await api.delete(`/superadmin/agencies/${o.org_id}`); toast.success("Agency deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const filtered = agencies.filter((o) => !q || (o.name || "").toLowerCase().includes(q.toLowerCase()) || (o.owner?.email || "").toLowerCase().includes(q.toLowerCase()));

  const Actions = ({ o }) => (
    <div className="flex shrink-0 gap-2">
      <Button size="sm" variant="outline" onClick={() => impersonate(o.owner_id)} data-testid={`sa-impersonate-${o.org_id}`}><UserCog className="mr-1.5 h-4 w-4" /> Impersonate</Button>
      <Button size="icon" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => del(o)} data-testid={`sa-delete-${o.org_id}`}><Trash2 className="h-4 w-4" /></Button>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="sa-agencies-panel">
      <Card className="flex flex-wrap items-center gap-3 p-4">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search agencies or owners…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="sa-agency-search" />
        </div>
        <div className="flex rounded-lg border border-border p-0.5">
          <button onClick={() => setView("grid")} data-testid="sa-view-grid" className={`rounded-md p-1.5 ${view === "grid" ? "bg-primary/10 text-primary" : "text-muted-foreground"}`}><LayoutGrid className="h-4 w-4" /></button>
          <button onClick={() => setView("list")} data-testid="sa-view-list" className={`rounded-md p-1.5 ${view === "list" ? "bg-primary/10 text-primary" : "text-muted-foreground"}`}><List className="h-4 w-4" /></button>
        </div>
        <Button variant="outline" onClick={exportCsv} data-testid="sa-export-agencies-button"><Download className="mr-2 h-4 w-4" /> Export CSV</Button>
        <AddAgencyDialog onDone={load} />
      </Card>

      {filtered.length === 0 && <p className="p-8 text-center text-muted-foreground" data-testid="sa-agencies-empty">No agencies yet.</p>}

      {view === "grid" ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((o) => (
            <Card key={o.org_id} className="flex flex-col gap-3 p-5" data-testid={`sa-agency-${o.org_id}`}>
              <div className="flex items-center gap-3">
                <Avatar className="h-11 w-11 rounded-xl"><AvatarFallback className="rounded-xl bg-primary/15 text-primary">{initials(o.name)}</AvatarFallback></Avatar>
                <div className="min-w-0">
                  <p className="truncate font-heading font-semibold">{o.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{o.owner?.email || "No owner"}</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Users2 className="h-3 w-3" /> {o.users}</span>
                <span className="flex items-center gap-1"><ListChecks className="h-3 w-3" /> {o.leads} leads</span>
                {o.plan && <Badge variant="outline" className="capitalize">{o.plan}{o.status ? ` · ${o.status}` : ""}</Badge>}
              </div>
              <div className="mt-auto pt-2"><Actions o={o} /></div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="sa-agencies-table">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                <th className="px-4 py-3 font-medium">Agency</th>
                <th className="px-4 py-3 font-medium">Owner</th>
                <th className="px-4 py-3 font-medium">Users</th>
                <th className="px-4 py-3 font-medium">Leads</th>
                <th className="px-4 py-3 font-medium">Plan</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <tr key={o.org_id} className="border-b border-border/60" data-testid={`sa-agency-row-${o.org_id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8 rounded-lg"><AvatarFallback className="rounded-lg bg-primary/15 text-primary text-xs">{initials(o.name)}</AvatarFallback></Avatar>
                      <span className="font-medium">{o.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{o.owner?.email || "—"}</td>
                  <td className="px-4 py-3 font-mono">{o.users}</td>
                  <td className="px-4 py-3 font-mono">{o.leads}</td>
                  <td className="px-4 py-3">{o.plan ? <Badge variant="outline" className="capitalize">{o.plan}</Badge> : <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-4 py-3"><div className="flex justify-end"><Actions o={o} /></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

export default function Directory() {
  const { user } = useAuth();
  const canManage = user.role === "admin" || user.role === "manager";
  const [tab, setTab] = useState("discover");
  const [orgs, setOrgs] = useState([]);
  const [parts, setParts] = useState([]);
  const [rules, setRules] = useState([]);
  const [typeFilter, setTypeFilter] = useState("all");
  const [q, setQ] = useState("");
  const [rule, setRule] = useState({ partner_org_id: "", kind: "zip", value: "" });
  const [collapsed, setCollapsed] = useState({});
  const [viewMode, setViewMode] = useState("grid");
  const isSA = user.role === "superadmin";

  const loadDirectory = useCallback(async () => {
    const params = {};
    if (typeFilter !== "all") params.insurance_type = typeFilter;
    if (q) params.q = q;
    try { const { data } = await api.get("/directory", { params }); setOrgs(data); } catch {}
  }, [typeFilter, q]);
  const loadParts = useCallback(async () => { try { const { data } = await api.get("/partnerships"); setParts(data); } catch {} }, []);
  const loadRules = useCallback(async () => { try { const { data } = await api.get("/share-rules"); setRules(data); } catch {} }, []);

  useEffect(() => { loadDirectory(); }, [loadDirectory]);
  useEffect(() => { loadParts(); loadRules(); }, [loadParts, loadRules]);

  const connect = async (org_id) => {
    try { await api.post("/partnerships", { org_id }); toast.success("Partner request sent"); loadDirectory(); loadParts(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const accept = async (pid) => { try { await api.post(`/partnerships/${pid}/accept`); toast.success("Partnership accepted"); loadDirectory(); loadParts(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const decline = async (pid) => { try { await api.post(`/partnerships/${pid}/decline`); toast.success("Request declined"); loadDirectory(); loadParts(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const approveReq = async (pid) => { try { await api.post(`/partnerships/${pid}/approve`); toast.success("Approved — partner request sent"); loadDirectory(); loadParts(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const rejectReq = async (pid) => { try { await api.post(`/partnerships/${pid}/reject`); toast.success("Request rejected"); loadDirectory(); loadParts(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const remove = async (pid) => { if (!window.confirm("Remove this partnership? Shared leads will be revoked.")) return; try { await api.delete(`/partnerships/${pid}`); toast.success("Partnership removed"); loadDirectory(); loadParts(); loadRules(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  const accepted = parts.filter((p) => p.status === "accepted");
  const incoming = parts.filter((p) => p.status === "pending" && p.direction === "incoming");
  const outgoing = parts.filter((p) => p.status === "pending" && p.direction === "outgoing");
  const awaitingApproval = parts.filter((p) => p.status === "awaiting_approval");

  const addRule = async () => {
    if (!rule.partner_org_id) return toast.error("Choose a partner agency");
    if (!rule.value.trim()) return toast.error("Enter a value");
    try { const { data } = await api.post("/share-rules", rule); toast.success(`Rule saved · auto-shared ${data.applied_to} existing lead(s)`); setRule({ partner_org_id: "", kind: "zip", value: "" }); loadRules(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const delRule = async (id) => { try { await api.delete(`/share-rules/${id}`); toast.success("Rule removed"); loadRules(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  const statusButton = (o) => {
    if (o.is_self) return <Badge variant="outline" data-testid={`dir-self-${o.org_id}`}>Your agency</Badge>;
    const p = o.partnership;
    if (p?.status === "accepted") return <Badge className="bg-primary/15 text-primary" data-testid={`dir-connected-${o.org_id}`}><Check className="mr-1 h-3 w-3" /> Connected</Badge>;
    if (p?.status === "awaiting_approval") return <Badge variant="outline" className="text-amber-600" data-testid={`dir-awaiting-${o.org_id}`}><ShieldAlert className="mr-1 h-3 w-3" /> Pending admin approval</Badge>;
    if (p?.status === "pending" && p.direction === "outgoing") return <Badge variant="outline" data-testid={`dir-pending-${o.org_id}`}><Clock className="mr-1 h-3 w-3" /> Pending</Badge>;
    if (p?.status === "pending" && p.direction === "incoming")
      return <Button size="sm" onClick={() => accept(p.partnership_id)} data-testid={`dir-accept-${o.org_id}`}><Check className="mr-1.5 h-4 w-4" /> Accept</Button>;
    return <Button size="sm" variant="outline" onClick={() => connect(o.org_id)} data-testid={`dir-connect-${o.org_id}`}><UserPlus className="mr-1.5 h-4 w-4" /> Connect</Button>;
  };

  const OrgCard = ({ o }) => (
    <Card className="flex flex-col gap-3 p-5" data-testid={`directory-org-${o.org_id}`}>
      <div className="flex items-center gap-3">
        <Avatar className="h-11 w-11 rounded-xl">
          {o.logo && <AvatarImage src={fileUrl(o.logo)} alt="" className="object-contain" />}
          <AvatarFallback className="rounded-xl bg-primary/15 text-primary">{initials(o.name)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <p className="truncate font-heading font-semibold">{o.name}</p>
          <p className="flex items-center gap-2 text-xs text-muted-foreground"><Users2 className="h-3 w-3" /> {o.member_count} · <ListChecks className="h-3 w-3" /> {o.lead_count} leads</p>
        </div>
      </div>
      <TypeBadges types={o.insurance_types} />
      <div className="mt-auto pt-2">{statusButton(o)}</div>
    </Card>
  );

  const OrgRow = ({ o }) => (
    <div className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`directory-org-row-${o.org_id}`}>
      <Avatar className="h-9 w-9 rounded-lg">
        {o.logo && <AvatarImage src={fileUrl(o.logo)} alt="" className="object-contain" />}
        <AvatarFallback className="rounded-lg bg-primary/15 text-primary text-xs">{initials(o.name)}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{o.name}</p>
        <p className="flex items-center gap-2 text-xs text-muted-foreground"><Users2 className="h-3 w-3" /> {o.member_count} · <ListChecks className="h-3 w-3" /> {o.lead_count} leads</p>
      </div>
      <div className="hidden md:block"><TypeBadges types={o.insurance_types} /></div>
      <div className="ml-auto">{statusButton(o)}</div>
    </div>
  );

  const GroupSection = ({ id, label, color, icon: Icon, list }) => {
    if (!list.length) return null;
    const open = !collapsed[id];
    return (
      <Card className="overflow-hidden" data-testid={`dir-group-${id}`}>
        <button onClick={() => setCollapsed((p) => ({ ...p, [id]: !p[id] }))} data-testid={`dir-group-toggle-${id}`}
          className="flex w-full items-center justify-between px-5 py-3.5 text-left hover:bg-accent/50">
          <span className="flex items-center gap-2 font-heading font-semibold" style={color ? { color } : undefined}>
            <Icon className="h-4 w-4" /> {label}
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">{list.length}</span>
          </span>
          {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        </button>
        {open && (viewMode === "list"
          ? <div className="divide-y divide-border/60 border-t border-border">{list.map((o) => <OrgRow key={o.org_id} o={o} />)}</div>
          : <div className="grid gap-4 border-t border-border p-4 sm:grid-cols-2 lg:grid-cols-3">{list.map((o) => <OrgCard key={o.org_id} o={o} />)}</div>)}
      </Card>
    );
  };

  const TabBtn = ({ id, icon: Icon, children, count }) => (
    <button onClick={() => setTab(id)} data-testid={`dir-tab-${id}`}
      className={`flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors ${tab === id ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-accent"}`}>
      <Icon className="h-4 w-4" /> {children}
      {count > 0 && <span className="rounded-full bg-primary px-1.5 text-[11px] font-bold text-primary-foreground">{count}</span>}
    </button>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold sm:text-3xl">Agency directory</h1>
        <p className="mt-1 text-sm text-muted-foreground">Discover agencies by insurance type, partner up, and share leads across agencies.</p>
      </div>

      {isSA && <SuperAdminAgencies />}
      {!isSA && <>
      <div className="flex flex-wrap gap-2">
        <TabBtn id="discover" icon={Building2}>Discover</TabBtn>
        <TabBtn id="partners" icon={Network} count={incoming.length + (canManage ? awaitingApproval.length : 0)}>Partnerships</TabBtn>
        <TabBtn id="rules" icon={ListChecks}>Auto-share rules</TabBtn>
      </div>

      {tab === "discover" && (
        <>
          <Card className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative min-w-[200px] flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input placeholder="Search agencies…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="directory-search" />
              </div>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[190px]" data-testid="directory-type-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All insurance types</SelectItem>
                  {CATEGORY_LIST.map((k) => <SelectItem key={k} value={k}>{CATEGORIES[k].label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex rounded-lg border border-border p-0.5" data-testid="directory-view-toggle">
                <button onClick={() => setViewMode("grid")} data-testid="directory-view-grid" className={`rounded-md p-1.5 ${viewMode === "grid" ? "bg-primary/10 text-primary" : "text-muted-foreground"}`}><LayoutGrid className="h-4 w-4" /></button>
                <button onClick={() => setViewMode("list")} data-testid="directory-view-list" className={`rounded-md p-1.5 ${viewMode === "list" ? "bg-primary/10 text-primary" : "text-muted-foreground"}`}><List className="h-4 w-4" /></button>
              </div>
            </div>
          </Card>
          <div className="space-y-4" data-testid="directory-grouped">
            {orgs.length === 0 && <p className="p-8 text-center text-muted-foreground" data-testid="directory-empty">No agencies match your filters.</p>}
            {CATEGORY_LIST.map((k) => (
              <GroupSection key={k} id={k} label={CATEGORIES[k].label} color={CATEGORIES[k].color} icon={CATEGORIES[k].icon}
                list={orgs.filter((o) => (o.insurance_types || []).includes(k))} />
            ))}
            {typeFilter === "all" && (
              <GroupSection id="unlisted" label="Unlisted products" icon={Building2}
                list={orgs.filter((o) => !(o.insurance_types || []).length)} />
            )}
          </div>
        </>
      )}

      {tab === "partners" && (
        <div className="space-y-6">
          {awaitingApproval.length > 0 && (
            <Card className="p-5 border-amber-500/40" data-testid="awaiting-approval-card">
              <h3 className="flex items-center gap-2 font-heading font-semibold"><ShieldAlert className="h-4 w-4 text-amber-500" /> Awaiting admin approval</h3>
              <p className="mt-1 text-xs text-muted-foreground">{canManage ? "Agent-initiated partner requests. Approve to send them to the other agency." : "Your requests are pending your admin's approval before they're sent."}</p>
              <div className="mt-3 space-y-2" data-testid="awaiting-list">
                {awaitingApproval.map((p) => (
                  <div key={p.partnership_id} className="flex items-center justify-between rounded-lg border border-border p-3">
                    <span className="font-medium">{p.other_org_name}</span>
                    {canManage ? (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => approveReq(p.partnership_id)} data-testid={`approve-req-${p.partnership_id}`}><Check className="mr-1.5 h-4 w-4" /> Approve & send</Button>
                        <Button size="sm" variant="outline" className="text-destructive" onClick={() => rejectReq(p.partnership_id)} data-testid={`reject-req-${p.partnership_id}`}><X className="mr-1.5 h-4 w-4" /> Reject</Button>
                      </div>
                    ) : <Badge variant="outline" className="text-amber-600"><Clock className="mr-1 h-3 w-3" /> Pending admin approval</Badge>}
                  </div>
                ))}
              </div>
            </Card>
          )}
          {incoming.length > 0 && (
            <Card className="p-5">
              <h3 className="font-heading font-semibold">Incoming requests</h3>
              <div className="mt-3 space-y-2" data-testid="incoming-list">
                {incoming.map((p) => (
                  <div key={p.partnership_id} className="flex items-center justify-between rounded-lg border border-border p-3">
                    <span className="font-medium">{p.other_org_name}</span>
                    {canManage ? (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => accept(p.partnership_id)} data-testid={`accept-${p.partnership_id}`}><Check className="mr-1.5 h-4 w-4" /> Accept</Button>
                        <Button size="sm" variant="outline" onClick={() => decline(p.partnership_id)} data-testid={`decline-${p.partnership_id}`}><X className="mr-1.5 h-4 w-4" /> Decline</Button>
                      </div>
                    ) : <Badge variant="outline">Awaiting your admin</Badge>}
                  </div>
                ))}
              </div>
            </Card>
          )}
          <Card className="p-5">
            <h3 className="font-heading font-semibold">Connected agencies</h3>
            <div className="mt-3 space-y-2" data-testid="connected-list">
              {accepted.length === 0 && <p className="text-sm text-muted-foreground">No partnerships yet. Connect with agencies from the Discover tab.</p>}
              {accepted.map((p) => (
                <div key={p.partnership_id} className="flex items-center justify-between rounded-lg border border-border p-3">
                  <span className="font-medium">{p.other_org_name}</span>
                  {canManage && <Button size="icon" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => remove(p.partnership_id)} data-testid={`remove-${p.partnership_id}`}><Trash2 className="h-4 w-4" /></Button>}
                </div>
              ))}
            </div>
          </Card>
          {outgoing.length > 0 && (
            <Card className="p-5">
              <h3 className="font-heading font-semibold">Sent requests</h3>
              <div className="mt-3 space-y-2" data-testid="outgoing-list">
                {outgoing.map((p) => (
                  <div key={p.partnership_id} className="flex items-center justify-between rounded-lg border border-border p-3">
                    <span className="font-medium">{p.other_org_name}</span>
                    <Badge variant="outline"><Clock className="mr-1 h-3 w-3" /> Pending</Badge>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {tab === "rules" && (
        <div className="space-y-6">
          <Card className="p-5">
            <h3 className="font-heading font-semibold">Auto-share a feed with a partner</h3>
            <p className="mt-1 text-sm text-muted-foreground">New and existing leads matching a ZIP, state, or product are shared automatically with the chosen partner agency.</p>
            {!canManage ? (
              <p className="mt-4 text-sm text-muted-foreground">Only admins and managers can create auto-share rules.</p>
            ) : accepted.length === 0 ? (
              <p className="mt-4 text-sm text-muted-foreground">Connect with a partner agency first to set up sharing.</p>
            ) : (
              <div className="mt-4 grid gap-3 md:grid-cols-[1.4fr_1fr_1.2fr_auto] md:items-end">
                <div>
                  <Label>Partner agency</Label>
                  <Select value={rule.partner_org_id} onValueChange={(v) => setRule({ ...rule, partner_org_id: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="rule-partner-select"><SelectValue placeholder="Select agency" /></SelectTrigger>
                    <SelectContent>{accepted.map((p) => <SelectItem key={p.other_org_id} value={p.other_org_id}>{p.other_org_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Match by</Label>
                  <Select value={rule.kind} onValueChange={(v) => setRule({ ...rule, kind: v, value: "" })}>
                    <SelectTrigger className="mt-1.5" data-testid="rule-kind-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="zip">ZIP code</SelectItem>
                      <SelectItem value="state">State</SelectItem>
                      <SelectItem value="product">Insurance product</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{rule.kind === "product" ? "Product" : rule.kind === "state" ? "State (e.g. CA)" : "ZIP code"}</Label>
                  {rule.kind === "product" ? (
                    <Select value={rule.value} onValueChange={(v) => setRule({ ...rule, value: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="rule-value-select"><SelectValue placeholder="Select product" /></SelectTrigger>
                      <SelectContent>{CATEGORY_LIST.map((k) => <SelectItem key={k} value={k}>{CATEGORIES[k].label}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <Input className="mt-1.5" value={rule.value} onChange={(e) => setRule({ ...rule, value: e.target.value })} placeholder={rule.kind === "state" ? "CA" : "90012"} data-testid="rule-value-input" />
                  )}
                </div>
                <Button onClick={addRule} data-testid="add-rule-button"><Plus className="mr-2 h-4 w-4" /> Add rule</Button>
              </div>
            )}
          </Card>
          <Card className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="rules-table">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Partner</th>
                  <th className="px-4 py-3 font-medium">Match</th>
                  <th className="px-4 py-3 font-medium">Value</th>
                  <th className="px-4 py-3 text-right font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {rules.length === 0 ? (
                  <tr><td colSpan={4} className="p-8 text-center text-muted-foreground" data-testid="rules-empty">No auto-share rules yet.</td></tr>
                ) : rules.map((r) => (
                  <tr key={r.rule_id} className="border-b border-border/60" data-testid={`rule-row-${r.rule_id}`}>
                    <td className="px-4 py-3 font-medium">{r.partner_org_name}</td>
                    <td className="px-4 py-3 capitalize">{r.kind}</td>
                    <td className="px-4 py-3">{r.kind === "product" ? (CATEGORIES[r.value]?.label || r.value) : r.value}</td>
                    <td className="px-4 py-3 text-right">
                      {canManage && <Button size="icon" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => delRule(r.rule_id)} data-testid={`del-rule-${r.rule_id}`}><Trash2 className="h-4 w-4" /></Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}
      </>}
    </div>
  );
}
